
import { defineComponent } from "vue";
import {
  IBienBanGiaiTheResponseBase,
  IVatTuDinhMucCoDinh,
  IVatTuNgoaiDinhMuc,
} from "@/core/interfaces/ApiResponses";
import { BienBanGiaiTheService } from "@/core/services/BienBanGiaiThe.service";
import { DinhMucCoDinhService } from "@/core/services/DinhMucCoDinh.service";
import { formatQuantity } from "@/core/helpers/number-format";
import { swalErrNotification } from "@/core/helpers/utils";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import PdfViewerPage from "@/components/shared/pdf-viewer/PdfViewerPage.vue";
import PdfDocumentHeader from "@/components/shared/pdf-viewer/PdfDocumentHeader.vue";
import { generateHtmlElementPdf } from "@/components/shared/pdf-viewer/generate-html-element-pdf";
import { pickDonViPdfExtra } from "@/components/shared/pdf-viewer/pdf-document-header";
import {
  getPdfContentWidthPx,
  getPdfPrintContentStyle,
} from "@/components/shared/pdf-viewer/pdf-content.config";

export default defineComponent({
  name: "BienBanGiaiTheViewer",

  components: { PdfViewerPage, PdfDocumentHeader },

  setup() {
    const { formatDisplayingDate } = useDateTime();
    return { formatDisplayingDate };
  },

  data() {
    return {
      bienBanData: null as IBienBanGiaiTheResponseBase | null,
      vatTuDmcdItems: [] as IVatTuDinhMucCoDinh[],
      pdfSource: null as string | null,
      isGenerating: false,
      tenDonVi: "",
      tenDonViCha: "",
    };
  },

  computed: {
    pdfFileName(): string {
      if (!this.bienBanData) return "bien-ban-giai-the.pdf";
      return `bien-ban-giai-the-${this.bienBanData.so_bien_ban}.pdf`;
    },

    ngayBienBanDisplay(): string {
      if (this.bienBanData?.ngay_lap) {
        const date = new Date(this.bienBanData.ngay_lap);
        return `Ngày ${date.getDate()} tháng ${
          date.getMonth() + 1
        } năm ${date.getFullYear()}`;
      }
      return ".........................";
    },

    capSuaChuaDisplay(): string {
      if (!this.bienBanData) return "";
      return (
        this.bienBanData.cap_sua_chua?.ten_cap_sc ||
        this.bienBanData.ke_hoach_sua_chua?.cap_sua_chua?.ten_cap_sc ||
        ""
      );
    },

    ngayVaoXuongDisplay(): string {
      if (!this.bienBanData?.ngay_vao_xuong) return "";
      return this.formatDisplayingDate(this.bienBanData.ngay_vao_xuong, "/");
    },

    signatureItems(): { label: string }[] {
      return [
        { label: "KTV GIẢI THỂ" },
        { label: "P.KT hoặc ATNV" },
        { label: "P.KHVT" },
        { label: "GIÁM ĐỐC" },
      ];
    },

    printPaperStyle(): Record<string, string> {
      return getPdfPrintContentStyle();
    },

    hiddenTemplateStyle(): Record<string, string> {
      return { width: `${getPdfContentWidthPx()}px` };
    },
  },

  methods: {
    formatQuantity,

    getVatTuGhiChu(vatTu: IVatTuDinhMucCoDinh | IVatTuNgoaiDinhMuc): string {
      return (vatTu.pivot as { ghi_chu?: string }).ghi_chu ?? "";
    },

    async loadVatTuDmcd() {
      if (!this.bienBanData) return;

      const dinhMucResponse =
        await DinhMucCoDinhService.getDinhMucCoDinhByIdCapSuaChuaAndDMTX({
          id_cap_sc:
            this.bienBanData.ke_hoach_sua_chua?.cap_sua_chua?.id ||
            this.bienBanData.cap_sua_chua?.id,
          id_dm_toa_xe:
            this.bienBanData.ke_hoach_sua_chua?.id_danh_muc_toa_xe ||
            this.bienBanData.toa_xe.danh_muc_toa_xe[0].id,
        });

      if (!dinhMucResponse) {
        this.vatTuDmcdItems = [];
        return;
      }

      const {
        data: {
          data: { vat_tu: vatTuItemsResponse },
        },
      } = await DinhMucCoDinhService.get(dinhMucResponse.id);

      if (vatTuItemsResponse) {
        this.vatTuDmcdItems = vatTuItemsResponse.map((item) => ({
          ...item,
          pivot: {
            id_dinh_muc: dinhMucResponse.id,
            id_vat_tu: item.id,
            so_luong: item.pivot.so_luong,
          },
        }));
      }
    },

    async loadAndGenerate() {
      const id = Number(this.$route.params.id);
      if (!id) return;

      try {
        this.isGenerating = true;
        const { data: response } = await BienBanGiaiTheService.get(id);
        this.bienBanData = response.data;
        const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(response.extra);
        this.tenDonVi = tenDonVi;
        this.tenDonViCha = tenDonViCha;

        await this.loadVatTuDmcd();

        await this.$nextTick();
        await this.$nextTick();

        await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, "Có lỗi khi tải biên bản giải thể");
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf() {
      const el = (this.$refs.printArea as HTMLElement)?.querySelector(
        ".printing-paper"
      ) as HTMLElement | null;
      if (!el) return;

      this.pdfSource = await generateHtmlElementPdf(el);
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
